import { Star, Quote } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const REVIEWS = [
  {
    id: "aubry",
    name: "Claire A.",
    role: "Boulangère — Maison Aubry",
    rating: 5,
    text: "En une semaine, notre boutique avait enfin un site clair avec nos horaires et nos spécialités. Les clients nous disent qu'ils nous ont trouvés sur Google.",
  },
  {
    id: "delorme",
    name: "Maître Delorme",
    role: "Avocat — Cabinet Delorme",
    rating: 5,
    text: "Un rendu sobre et sérieux, exactement ce qu'attend un cabinet. La prise de rendez-vous en ligne nous a fait gagner un temps précieux.",
  },
  {
    id: "horizon",
    name: "Sophie R.",
    role: "Agente immobilière — Horizon Immobilier",
    rating: 4.5,
    text: "Le catalogue de biens est simple à mettre à jour et les demandes de visite arrivent directement par email. Accompagnement réactif du début à la fin.",
  },
  {
    id: "bois",
    name: "Julien M.",
    role: "Menuisier — Atelier Bois & Sens",
    rating: 5,
    text: "Je n'y connais rien en informatique et pourtant tout a été fluide. La galerie met vraiment en valeur mon travail, et les devis rentrent.",
  },
];

const Stars = ({ rating, testId }) => (
  <span className="flex items-center gap-0.5" aria-label={`${rating} sur 5`} data-testid={testId}>
    {[1, 2, 3, 4, 5].map((i) => {
      const fill = rating >= i ? 100 : rating >= i - 0.5 ? 50 : 0;
      return (
        <span key={i} className="relative h-4 w-4">
          <Star size={16} className="absolute inset-0 text-navy/15" fill="currentColor" strokeWidth={0} />
          <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill}%` }}>
            <Star size={16} className="text-gold" fill="currentColor" strokeWidth={0} />
          </span>
        </span>
      );
    })}
    <span className="ml-1.5 font-mono text-[11px] font-semibold text-navy">{String(rating).replace(".", ",")}/5</span>
  </span>
);

const ReviewCard = ({ r }) => (
  <article data-testid={`testimonial-card-${r.id}`} className="flex h-full flex-col rounded-3xl border border-navy/10 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-xl hover:shadow-gold/10">
    <div className="flex items-center justify-between">
      <Stars rating={r.rating} testId={`testimonial-rating-${r.id}`} />
      <Quote size={18} className="text-gold/60" />
    </div>
    <p className="mt-5 flex-1 text-sm leading-relaxed text-slate-700">« {r.text} »</p>
    <div className="mt-6 border-t border-navy/10 pt-4">
      <p className="font-display text-base font-bold text-navy">{r.name}</p>
      <p className="mt-0.5 text-xs text-slate-500">{r.role}</p>
    </div>
  </article>
);

export const Testimonials = () => {
  const avg = REVIEWS.reduce((a, r) => a + r.rating, 0) / REVIEWS.length;
  return (
    <section id="avis" className="border-t border-navy/10 bg-white py-16 sm:py-24 lg:py-32" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <Reveal>
              <Eyebrow>Avis clients</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
                Ils nous ont confié leur vitrine.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.16}>
            <div className="flex items-center gap-3 rounded-full border border-navy/10 bg-cream px-5 py-3" data-testid="testimonials-average">
              <Stars rating={Math.round(avg * 2) / 2} testId="testimonials-average-stars" />
              <span className="text-xs text-slate-500">sur {REVIEWS.length} avis</span>
            </div>
          </Reveal>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4" data-testid="testimonials-grid">
          {REVIEWS.map((r, i) => (
            <Reveal key={r.id} delay={i * 0.08} className="h-full">
              <ReviewCard r={r} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
